import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { EVENT_TYPES, SERVICES_CHECKBOXES, BUDGET_RANGES, GUEST_RANGES } from "../data/content";
import { trackEvent, EVENTS } from "@/utils/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", phone: "", email: "",
  event_type: "", city: "", event_date: "",
  guest_count: "", budget_range: "", venue_booked: "",
  services_required: [], message: "",
};

export default function InquiryForm({ compact = false, source = "website", title = "Tell us about your event", subtitle = "We'll respond within one business day with a curated next step." }) {
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleService = (s) =>
    setData((d) => ({
      ...d,
      services_required: d.services_required.includes(s)
        ? d.services_required.filter((x) => x !== s)
        : [...d.services_required, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.phone || !data.email) {
      toast.error("Please share your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...data, source });
      trackEvent(EVENTS.CONTACT_FORM_SUBMIT, {
        location: source,
        event_type: data.event_type || "unspecified",
        city: data.city || "",
        content_name: "Inquiry Form",
        content_category: data.event_type || "Event Inquiry",
      });
      toast.success("Thank you. We'll be in touch shortly.");
      setData(empty);
    } catch {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="inquiry-form" className="lux-card p-8 md:p-10">
      <div className="mb-8">
        <p className="pill" data-testid="inquiry-pill">Plan With Us</p>
        <h3 className="font-display text-3xl md:text-4xl mt-4 text-[#3B312D]">{title}</h3>
        <p className="mt-3 text-[15px] text-[#36454F]/80 max-w-xl">{subtitle}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input className="lux-input" placeholder="Full name" value={data.name} onChange={(e) => update("name", e.target.value)} required data-testid="inquiry-name" />
        <input className="lux-input" placeholder="Phone number" value={data.phone} onChange={(e) => update("phone", e.target.value)} required data-testid="inquiry-phone" />
        <input type="email" className="lux-input" placeholder="Email address" value={data.email} onChange={(e) => update("email", e.target.value)} required data-testid="inquiry-email" />
        <select className="lux-select" value={data.event_type} onChange={(e) => update("event_type", e.target.value)} data-testid="inquiry-event-type">
          <option value="">Event type</option>
          {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
        <input className="lux-input" placeholder="City / location" value={data.city} onChange={(e) => update("city", e.target.value)} data-testid="inquiry-city" />
        <input type="date" className="lux-input" value={data.event_date} onChange={(e) => update("event_date", e.target.value)} data-testid="inquiry-date" />
        <select className="lux-select" value={data.guest_count} onChange={(e) => update("guest_count", e.target.value)} data-testid="inquiry-guests">
          <option value="">Guest count</option>
          {GUEST_RANGES.map((g) => <option key={g}>{g}</option>)}
        </select>
        <select className="lux-select" value={data.budget_range} onChange={(e) => update("budget_range", e.target.value)} data-testid="inquiry-budget">
          <option value="">Budget range</option>
          {BUDGET_RANGES.map((b) => <option key={b}>{b}</option>)}
        </select>
        <select className="lux-select md:col-span-2" value={data.venue_booked} onChange={(e) => update("venue_booked", e.target.value)} data-testid="inquiry-venue-booked">
          <option value="">Venue booked?</option>
          <option>Yes</option>
          <option>No</option>
          <option>Help me find one</option>
        </select>
      </div>

      {!compact && (
        <div className="mt-6">
          <p className="text-[11px] tracking-[0.22em] uppercase text-[#C5A059] mb-3">Services required</p>
          <div className="flex flex-wrap gap-2">
            {SERVICES_CHECKBOXES.map((s) => {
              const active = data.services_required.includes(s);
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleService(s)}
                  data-testid={`inquiry-service-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-2 rounded-full text-[12px] tracking-[0.12em] uppercase border transition ${
                    active
                      ? "bg-[#3B312D] text-[#FAF6F1] border-[#3B312D]"
                      : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.15)] hover:border-[#C5A059] hover:text-[#C5A059]"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <textarea
        rows={4}
        className="lux-textarea mt-6"
        placeholder="Tell us a little about your vision, dates, or any specifics…"
        value={data.message}
        onChange={(e) => update("message", e.target.value)}
        data-testid="inquiry-message"
      />

      <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-[12px] text-[#36454F]/70">
          By submitting, you agree to be contacted by our team.
        </p>
        <button type="submit" disabled={loading} className="btn-primary" data-testid="inquiry-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
          {loading ? "Sending…" : "Send Inquiry"}
        </button>
      </div>
    </form>
  );
}
